import React, { createContext, useContext, useEffect, useState } from "react";
import { io, Socket } from "socket.io-client";
import { useAppSelector, useAppDispatch } from "../store/hooks";
import { API_BASE_URL } from "../api/apiClient";
import { apiSlice } from "../store/apiSlice";
import { fetchAllData } from "../store/slices/dataSlice";
import { fetchDocuments } from "../store/slices/documentSlice";

interface SocketContextType {
  socket: Socket | null;
}

const SocketContext = createContext<SocketContextType>({ socket: null });

export const useSocket = () => useContext(SocketContext);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const user = useAppSelector((state) => state.auth.user);
  const dispatch = useAppDispatch();

  useEffect(() => {
    // Only connect if user is logged in
    if (!user?._id) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
      }
      return;
    }

    const socketUrl = API_BASE_URL.replace(/\/api$/, "");

    const newSocket = io(socketUrl, {
      path: "/api/socket.io",
      transports: ["websocket", "polling"],
    });

    newSocket.on("connect", () => {
      newSocket.emit("join-room", user._id);
      if (user.companyId) {
        newSocket.emit("join-company", user.companyId);
      }
    });

    // Handle live updates
    newSocket.on("invalidate-tag", (tag: string) => {
      // Update RTK Query components
      dispatch(apiSlice.util.invalidateTags([tag as any]));

      // Update global standard components
      dispatch(fetchAllData());

      // Update document specific components
      if (tag === "CompanyDocument") {
        dispatch(fetchDocuments());
      }
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [user?._id]);

  return (
    <SocketContext.Provider value={{ socket }}>
      {children}
    </SocketContext.Provider>
  );
};
